import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/apiClient';

export const searchKeys = {
  all: ['search'] as const,
  results: (term: string) => [...searchKeys.all, term] as const,
};

/** Debounces a value by the given delay in ms. */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Global search query hook.
 * Only fires when the debounced term is at least 2 characters.
 */
export const useSearchQuery = (term: string, limit = 10) => {
  const debouncedTerm = useDebounce(term.trim(), 300);
  return useQuery({
    queryKey: searchKeys.results(debouncedTerm),
    queryFn: () => apiClient.search(debouncedTerm, limit),
    enabled: debouncedTerm.length >= 2,
    staleTime: 10 * 1000,
    gcTime: 60 * 1000,
  });
};
